import { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { ShopContext } from "./contexts";
import { useApi } from "@hooks/useApi";

export const ShopProvider = ({ children }) => {
    const api = useApi();
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const shopRequest = useCallback(async (endpoint, method, data, options = {}) => {
        const requestPath = `shop${endpoint}`;
        const requestMethod = method || "GET";

        if (requestMethod === "GET") {
            return api.get(requestPath, options);
        }

        if (requestMethod === "POST") {
            return api.post(requestPath, data, options);
        }

        if (requestMethod === "PUT") {
            return api.put(requestPath, data, options);
        }

        return api.delete(requestPath, options);
    }, [api]);

    // Product operations
    const fetchProducts = useCallback(
        async (page = 1, limit = 20, search = "") => {
            setLoading(true);
            setError(null);
            try {
                const query = new URLSearchParams({ page, limit });
                if (search) query.append("search", search);

                const data = await shopRequest(`/products?${query}`);
                setProducts(data.data.products);
                return data.data;
            } catch (err) {
                setError(err.message);
                console.error("Failed to fetch products:", err);
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const fetchProductById = useCallback(
        async (productId) => {
            setLoading(true);
            setError(null);
            try {
                const data = await shopRequest(`/products/${productId}`);
                return data.data;
            } catch (err) {
                setError(err.message);
                console.error("Failed to fetch product:", err);
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const createProduct = useCallback(
        async (productData) => {
            setLoading(true);
            setError(null);
            try {
                const data = await shopRequest("/products", "POST", productData);
                return data.data;
            } catch (err) {
                setError(err.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const updateProduct = useCallback(
        async (productId, updates) => {
            setLoading(true);
            setError(null);
            try {
                const data = await shopRequest(`/products/${productId}`, "PUT", updates);
                return data.data;
            } catch (err) {
                setError(err.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const deleteProduct = useCallback(
        async (productId) => {
            setLoading(true);
            setError(null);
            try {
                await shopRequest(`/products/${productId}`, "DELETE");
                setProducts((prev) => prev.filter((p) => p._id !== productId));
                return true;
            } catch (err) {
                setError(err.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    // Order operations
    const createOrder = useCallback(
        async (productId, quantity = 1, deliveryAddress = null) => {
            setLoading(true);
            setError(null);
            try {
                // Same CSPRNG key as the bulk checkout in utils/shoppingCart.js
                const idempotencyKey = `order-${productId}-${Date.now()}-${globalThis.crypto.randomUUID()}`;
        
                const orderBody = { productId, quantity };
                if (deliveryAddress) {
                    orderBody.deliveryAddress = deliveryAddress;
                }

                const data = await shopRequest("/orders", "POST", orderBody, {
                    headers: {
                        "Idempotency-Key": idempotencyKey,
                    },
                });

                return data.data;
            } catch (err) {
                setError(err.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const fetchOrders = useCallback(
        async (page = 1, limit = 20, status = null) => {
            setLoading(true);
            setError(null);
            try {
                const query = new URLSearchParams({ page, limit });
                if (status) query.append("status", status);

                const data = await shopRequest(`/orders?${query}`);
                setOrders(data.data.orders);
                return data.data;
            } catch (err) {
                setError(err.message);
                console.error("Failed to fetch orders:", err);
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const fetchOrderById = useCallback(
        async (orderId) => {
            setLoading(true);
            setError(null);
            try {
                const data = await shopRequest(`/orders/${orderId}`);
                return data.data;
            } catch (err) {
                setError(err.message);
                console.error("Failed to fetch order:", err);
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const updateOrderStatus = useCallback(
        async (orderId, status) => {
            setLoading(true);
            setError(null);
            try {
                const data = await shopRequest(`/orders/${orderId}/status`, "PUT", { status });
                return data.data;
            } catch (err) {
                setError(err.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    // Admin operations
    const fetchAllOrders = useCallback(
        async (page = 1, limit = 20, filters = {}) => {
            setLoading(true);
            setError(null);
            try {
                const query = new URLSearchParams({ page, limit });
                if (filters.status) query.append("status", filters.status);
                if (filters.userId) query.append("userId", filters.userId);

                const data = await shopRequest(`/admin/orders?${query}`);
                return data.data;
            } catch (err) {
                setError(err.message);
                console.error("Failed to fetch all orders:", err);
            } finally {
                setLoading(false);
            }
        },
        [shopRequest]
    );

    const fetchStatistics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await shopRequest("/admin/statistics");
            return data.data;
        } catch (err) {
            setError(err.message);
            console.error("Failed to fetch statistics:", err);
        } finally {
            setLoading(false);
        }
    }, [shopRequest]);

    // Memoised: every useShop() consumer re-renders when this object changes
    const value = useMemo(() => ({
        products,
        orders,
        loading,
        error,
        setError,
        // Product methods
        fetchProducts,
        fetchProductById,
        createProduct,
        updateProduct,
        deleteProduct,
        // Order methods
        createOrder,
        fetchOrders,
        fetchOrderById,
        updateOrderStatus,
        // Admin methods
        fetchAllOrders,
        fetchStatistics,
    }), [
        products, orders, loading, error, setError,
        fetchProducts, fetchProductById, createProduct, updateProduct, deleteProduct,
        createOrder, fetchOrders, fetchOrderById, updateOrderStatus,
        fetchAllOrders, fetchStatistics,
    ]);

    return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

ShopProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
